import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CruisesSearchPage from '../SearchPage/CruiseSearchPage'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import HeroImage from 'content/components/HeroImage'
import LayoutContainer from 'components/Common/LayoutContainer'
import styled from 'styled-components'
import { rem } from 'polished'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import GeoContext from 'contexts/geoContext'
import { decodeFromUrlString } from './CruiseSeoPageUtil'
import CruiseDeparturesPageHeadData from './CruiseDeparturesPageHeadData'
import CruiseDestinationsHeadData from './CruiseDestinationsHeadData'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { fetchPlaceByName } from 'actions/PlaceActions'
import { useAppDispatch } from 'hooks/reduxHooks'

const StyledHeroImage = styled(HeroImage)`
  min-height: ${rem(180)};
`

interface Props extends RouteComponentProps<{ departureLocation?: string, destinationLocation?: string }> {
  locationToPlace: App.State['destination']['locationToPlace']
  placeLookUp: App.State['destination']['places']
  placesLoading: App.State['destination']['placesLoading']
}

function CruiseLocationLandingPage(props: Props) {
  const pageType = props.match.path.includes('departures') ? 'departures' : 'destinations'
  const isDeparture = pageType === 'departures'
  const isDestination = pageType === 'destinations'
  const { locationToPlace, placeLookUp, placesLoading } = props
  const { departureLocation, destinationLocation } = props.match.params
  const location = decodeFromUrlString((isDeparture ? departureLocation : destinationLocation) as string)
  const locationKey = location.charAt(0).toUpperCase() + location.slice(1)
  const place = locationToPlace[locationKey]
  const isFetchingPlace = placesLoading[locationKey]
  const dispatch = useAppDispatch()
  const intl = useIntl()

  useEffect(() => {
    if (place === undefined && !isFetchingPlace) {
      dispatch(fetchPlaceByName(locationKey))
    }
  }, [dispatch, isFetchingPlace, locationKey, place])

  const filters = useMemo<App.OfferListFilters>(() => {
    if (!place) {
      return {
        departureNames: [],
        departureIds: [],
        destinationNames: [],
        destinationIds: [],
      }
    }

    return {
      departureNames: isDeparture ? [place.name] : [],
      departureIds: isDeparture ? [place.id] : [],
      destinationNames: isDestination ? [place.name] : [],
      destinationIds: isDestination ? [place.id] : [],
    }
  }, [isDeparture, isDestination, place])

  const [searchCount, fetchingSearchCount] = useCruiseSearchCount(filters, !place)
  const { currentRegionCode } = useContext(GeoContext)

  if (isFetchingPlace || place === undefined) {
    return <LoadingIndicator />
  }

  if (place === null) {
    return <RedirectWithStatus code={302} to={`/${currentRegionCode.toLowerCase()}/cruises`} withSearchAndHash={false} />
  }

  if (!fetchingSearchCount && searchCount === 0) {
    return <RedirectWithStatus code={302} to={`/${currentRegionCode.toLowerCase()}/cruises`} withSearchAndHash={false} />
  }

  const imageId = placeLookUp[place.id]?.imageId || '8d2cc64monviv73whp4'
  const title = isDeparture
    ? intl.formatMessage(
        {
          id: 'kEHie0-Bku_5dLJb6-j7',
          defaultMessage: 'Find Cruises from {placeName}',
          description: 'Hero heading for cruise departure location landing page',
        },
        { placeName: place.name },
      )
    : intl.formatMessage(
        {
          id: 'TjpqcZY2gJgHRb4qXAQq',
          defaultMessage: 'Find Cruises to {placeName}',
          description: 'Hero heading for cruise destination location landing page',
        },
        { placeName: place.name },
      )
  const subtitle = intl.formatMessage(
    {
      id: 'hIn6GYRChSMviMaazzMn',
      defaultMessage: 'Escape with our luxury cruises deals in {placeName}',
      description: 'Hero subtitle for cruise location landing page promoting deals',
    },
    { placeName: place.name },
  )

  return (
    <>
      {isDeparture && <CruiseDeparturesPageHeadData place={place} />}
      {isDestination && <CruiseDestinationsHeadData destination={location} />}
      <StyledHeroImage
        id={imageId}
        brightness={-25}
        fit="center"
        gravity="center"
      >
        <LayoutContainer relative>
          <Heading variant="heading2" align="center" as="h1" colour="dark-contrast">{title}</Heading>
          <BodyText variant="large" align="center" as="h2" colour="dark-contrast">{subtitle}</BodyText>
        </LayoutContainer>
      </StyledHeroImage>
      <CruisesSearchPage initFilter={filters} disableHeadData />
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  placeLookUp: state.destination.places,
  locationToPlace: state.destination.locationToPlace,
  placesLoading: state.destination.placesLoading,
})

export default connect(mapStateToProps)(CruiseLocationLandingPage)
